export interface DrillDownConfig<TData = any, TItem = any> {
  id: string;
  title: string;
  endpoint: string;
  buildUrl: (baseUrl: string, params: TItem) => string;
  includeAuth?: boolean;
  errorHandler?: (error: Error) => string;
  dataTransformer?: (data: any) => TData;
  breadcrumbConfig?: {
    label: string;
    getBreadcrumbItems: (data: TData) => Array<{ label: string; value?: string }>;
  };
  chartConfig?: {
    type: 'bar' | 'line' | 'pie' | 'area';
    dataKey: string;
    valueKey: string;
    color?: string | string[];
    height?: number;
  };
  tableConfig?: {
    columns: Array<{
      key: string;
      label: string;
      align?: 'left' | 'center' | 'right';
      format?: (value: any) => string;
      width?: string | number;
    }>;
    pagination?: boolean;
    rowsPerPage?: number;
  };
}

export interface DrillDownState<TData = any, TItem = any> {
  isModalOpen: boolean;
  data: TData | null;
  loading: boolean;
  error: string | null;
  selectedItem: TItem | null;
}

export interface DrillDownActions<TData = any, TItem = any> {
  openModal: (item: TItem) => Promise<void>;
  closeModal: () => void;
  retry: () => Promise<void>;
}

export interface UseDrillDownReturn<TData = any, TItem = any> 
  extends DrillDownState<TData, TItem>, 
          DrillDownActions<TData, TItem> {}

// Tipos específicos para diferentes contextos
export interface BaseDrillDownData {
  id?: string;
  nome?: string;
  total?: number;
  [key: string]: any;
}

export interface FornecedorDrillDownData extends BaseDrillDownData {
  fornecedor?: {
    codigo: string;
    nome: string;
    cnpj_cpf: string;
    municipio: string;
    uf: string;
    endereco_completo: string;
    total_compras: number;
    total_credito_icms: number;
    total_descontos: number;
    total_documentos: number;
    total_transacoes: number;
    quantidade_total: number;
    produtos_distintos: number;
    primeira_compra: string;
    ultima_compra: string;
    ticket_medio: number;
    posicao_ranking: number;
    total_fornecedores: number;
  };
  compras_por_mes?: Array<{
    mes: number;
    ano: number;
    valor_compras: number;
    credito_icms: number;
    documentos: number;
    transacoes: number;
    quantidade: number;
    descontos: number;
  }>;
  top_cfops?: Array<{
    cfop: string;
    valor_total: number;
    icms_total: number;
    transacoes: number;
    produtos_distintos: number;
  }>;
  principais_produtos?: Array<{
    cod_item: string;
    descricao_produto: string;
    valor_total: number;
    icms_total: number;
    quantidade_total: number;
    transacoes: number;
    preco_medio_unitario: number;
    primeira_compra_produto: string;
    ultima_compra_produto: string;
  }>;
  transacoes_detalhadas?: Array<{
    dt_emissao: string;
    num_doc: string;
    cfop: string;
    cod_item: string;
    descr_compl: string;
    vl_item: number;
    vl_icms: number;
    qtd: number;
    desconto: number;
    valor_documento: number;
  }>;
  analise_por_ncm?: Array<{
    ncm: string;
    descricao_ncm: string;
    valor_total: number;
    icms_total: number;
    quantidade_total: number;
    produtos_diferentes: number;
    transacoes: number;
  }>;
}

export interface SecaoDrillDownData extends BaseDrillDownData {
  secao?: {
    nome: string;
    total_vendas?: number;
    total_compras?: number;
    total_debito_icms?: number;
    total_credito_icms?: number;
    total_documentos: number;
    total_transacoes: number;
    posicao_ranking: number;
    total_secoes: number;
  };
  vendas_por_mes?: Array<{
    mes: number;
    ano: number;
    valor_vendas: number;
    debito_icms: number;
    documentos: number;
    transacoes: number;
  }>;
  compras_por_mes?: Array<{
    mes: number;
    ano: number;
    valor_compras: number;
    credito_icms: number;
    documentos: number;
    transacoes: number;
  }>;
  top_produtos?: Array<{
    cod_item: string;
    descricao_produto: string;
    valor_total: number;
    icms_total: number;
    quantidade_total: number;
    transacoes: number;
  }>;
  top_cfops?: Array<{
    cfop: string;
    valor_total: number;
    icms_total: number;
    transacoes: number;
  }>;
}

export interface RegiaoDrillDownData extends BaseDrillDownData {
  regiao?: {
    uf: string;
    total_vendas?: number;
    total_compras?: number;
    total_debito_icms?: number;
    total_credito_icms?: number;
    total_documentos: number;
    total_transacoes: number;
    posicao_ranking: number;
    total_regioes: number;
  };
  vendas_por_mes?: Array<{
    mes: number;
    ano: number;
    valor_vendas: number;
    debito_icms: number;
    documentos: number;
    transacoes: number;
  }>;
  compras_por_mes?: Array<{
    mes: number;
    ano: number;
    valor_compras: number;
    credito_icms: number;
    documentos: number;
    transacoes: number;
  }>;
  top_fornecedores?: Array<{
    nome: string;
    valor_compras: number;
    documentos: number;
  }>;
  top_clientes?: Array<{
    nome: string;
    valor_vendas: number;
    documentos: number;
  }>;
}

export interface ProdutoDrillDownData extends BaseDrillDownData {
  produto?: {
    cod_item: string;
    descricao_produto: string;
    ncm: string;
    descricao_ncm: string;
    total_vendas: number;
    total_compras: number;
    total_debito_icms: number;
    total_credito_icms: number;
    quantidade_total: number;
    transacoes: number;
    preco_medio_unitario: number;
    primeira_transacao: string;
    ultima_transacao: string;
  };
  transacoes_por_mes?: Array<{
    mes: number;
    ano: number;
    credito_icms: number;
    debito_icms: number;
    quantidade_entrada: number;
    quantidade_saida: number;
    valor_total_item: number;
    total_transacoes_credito: number;
    total_transacoes_debito: number;
  }>;
  top_cfops?: Array<{
    cfop: string;
    total_icms: number;
    total_transacoes: number;
  }>;
  resumo_cfop_aliquota?: Array<{
    cfop: string;
    aliquota_icms: number;
    total_icms: number;
    total_transacoes: number;
  }>;
  parceiros_comerciais?: {
    fornecedores: Array<{
      nome: string;
      total_compras: number;
      icms_compras: number;
      quantidade_comprada: number;
    }>;
    clientes: Array<{
      nome: string;
      total_vendas: number;
      icms_vendas: number;
      quantidade_vendida: number;
    }>;
  };
  transacoes_detalhadas?: Array<{
    dt_emissao: string;
    num_doc: string;
    cfop: string;
    parceiro: string;
    vl_item: number;
    vl_icms: number;
    qtd: number;
    tipo_operacao: 'entrada' | 'saida';
  }>;
}

export interface CompradorDrillDownData extends BaseDrillDownData {
  comprador?: {
    codigo: string;
    nome: string;
    cnpj_cpf: string;
    municipio: string;
    uf: string;
    endereco_completo: string;
    total_vendas: number;
    total_debito_icms: number;
    total_descontos: number;
    total_documentos: number;
    total_transacoes: number;
    quantidade_total: number;
    produtos_distintos: number;
    primeira_venda: string;
    ultima_venda: string;
    ticket_medio: number;
    posicao_ranking: number;
    total_compradores: number;
  };
  vendas_por_mes?: Array<{
    mes: number;
    ano: number;
    valor_vendas: number;
    debito_icms: number;
    documentos: number;
    transacoes: number;
    quantidade: number;
    descontos: number;
  }>;
  top_cfops?: Array<{
    cfop: string;
    valor_total: number;
    icms_total: number;
    transacoes: number;
    produtos_distintos: number;
  }>;
  principais_produtos?: Array<{
    cod_item: string;
    descricao_produto: string;
    valor_total: number;
    icms_total: number;
    quantidade_total: number;
    transacoes: number;
    preco_medio_unitario: number;
    primeira_venda_produto: string;
    ultima_venda_produto: string;
  }>;
  transacoes_detalhadas?: Array<{
    dt_emissao: string;
    num_doc: string;
    cfop: string;
    cod_item: string;
    descr_compl: string;
    vl_item: number;
    vl_icms: number;
    qtd: number;
    desconto: number;
    valor_documento: number;
  }>;
  analise_por_ncm?: Array<{
    ncm: string;
    descricao_ncm: string;
    valor_total: number;
    icms_total: number;
    quantidade_total: number;
    produtos_diferentes: number;
    transacoes: number;
  }>;
}

// Configurações pré-definidas para diferentes tipos de drill down
export const DRILL_DOWN_CONFIGS = {
  fornecedor: {
    id: 'fornecedor',
    title: 'Detalhes do Fornecedor',
    endpoint: 'sped-fiscal/fornecedor-detalhes-drill-down',
    buildUrl: (baseUrl: string, params: { nome: string }) => 
      `${baseUrl}sped-fiscal/fornecedor-detalhes-drill-down/${encodeURIComponent(params.nome)}`,
    includeAuth: true,
    breadcrumbConfig: {
      label: 'Fornecedor',
      getBreadcrumbItems: (data: FornecedorDrillDownData) => [
        { label: data.fornecedor?.nome || 'Fornecedor' }
      ]
    },
    chartConfig: {
      type: 'line' as const,
      dataKey: 'compras_por_mes',
      valueKey: 'valor_compras',
      color: ['#2E7D32', '#4CAF50'],
      height: 300
    },
    tableConfig: {
             columns: [
         { key: 'cfop', label: 'CFOP', align: 'left' as const },
         { key: 'valor_total', label: 'Valor Total', align: 'right' as const, format: (value: number) => `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` },
         { key: 'icms_total', label: 'ICMS Total', align: 'right' as const, format: (value: number) => `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` },
         { key: 'transacoes', label: 'Transações', align: 'right' as const }
       ],
      pagination: true,
      rowsPerPage: 10
    }
  },
  
  secao: {
    id: 'secao',
    title: 'Detalhes da Seção',
    endpoint: 'sped-fiscal/secao-vendas-drill-down',
    buildUrl: (baseUrl: string, params: { nome: string }) => 
      `${baseUrl}sped-fiscal/secao-vendas-drill-down/${encodeURIComponent(params.nome)}`,
    includeAuth: true,
    breadcrumbConfig: {
      label: 'Seção',
      getBreadcrumbItems: (data: SecaoDrillDownData) => [
        { label: data.secao?.nome || 'Seção' }
      ]
    },
    chartConfig: {
      type: 'line' as const,
      dataKey: 'vendas_por_mes',
      valueKey: 'valor_vendas',
      color: ['#1976D2', '#2196F3'],
      height: 300
    },
    tableConfig: {
             columns: [
         { key: 'cod_item', label: 'Código', align: 'left' as const },
         { key: 'descricao_produto', label: 'Produto', align: 'left' as const },
         { key: 'valor_total', label: 'Valor Total', align: 'right' as const, format: (value: number) => `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` },
         { key: 'icms_total', label: 'ICMS Total', align: 'right' as const, format: (value: number) => `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` },
         { key: 'quantidade_total', label: 'Quantidade', align: 'right' as const },
         { key: 'transacoes', label: 'Transações', align: 'right' as const }
       ],
      pagination: true,
      rowsPerPage: 10
    }
  },
  
  regiao: {
    id: 'regiao',
    title: 'Detalhes da Região',
    endpoint: 'sped-fiscal/regiao-vendas-drill-down',
    buildUrl: (baseUrl: string, params: { uf: string }) => 
      `${baseUrl}sped-fiscal/regiao-vendas-drill-down/${params.uf}`,
    includeAuth: true,
    breadcrumbConfig: {
      label: 'Região',
      getBreadcrumbItems: (data: RegiaoDrillDownData) => [
        { label: data.regiao?.uf || 'Região' }
      ]
    },
    chartConfig: {
      type: 'line' as const,
      dataKey: 'vendas_por_mes',
      valueKey: 'valor_vendas',
      color: ['#7B1FA2', '#9C27B0'],
      height: 300
    },
    tableConfig: {
             columns: [
         { key: 'nome', label: 'Cliente', align: 'left' as const },
         { key: 'valor_vendas', label: 'Vendas', align: 'right' as const, format: (value: number) => `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` },
         { key: 'documentos', label: 'Documentos', align: 'right' as const }
       ],
      pagination: true,
      rowsPerPage: 10
    }
  },
  
  produto: {
    id: 'produto',
    title: 'Detalhes do Produto',
    endpoint: 'produto-detalhes-drill-down',
    buildUrl: (baseUrl: string, params: { codItem: string }) => 
      `${baseUrl}produto-detalhes-drill-down/${params.codItem}`,
    includeAuth: true,
    breadcrumbConfig: {
      label: 'Produto',
      getBreadcrumbItems: (data: ProdutoDrillDownData) => [
        { label: data.produto?.descricao_produto || 'Produto' }
      ]
    },
    chartConfig: {
      type: 'line' as const,
      dataKey: 'transacoes_por_mes',
      valueKey: 'credito_icms',
      color: ['#D32F2F', '#F44336'],
      height: 300
    },
    tableConfig: {
             columns: [
         { key: 'cfop', label: 'CFOP', align: 'left' as const },
         { key: 'total_icms', label: 'Total ICMS', align: 'right' as const, format: (value: number) => `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` },
         { key: 'total_transacoes', label: 'Transações', align: 'right' as const }
       ],
      pagination: true,
      rowsPerPage: 10
    }
  }
}; 